import { useState, useEffect } from 'react'
import loadable from '@loadable/component'
import { Helmet } from "react-helmet-async"
import { arrayMoveImmutable }  from 'array-move'

import type {
    TPossibleScreens,
    TUpload,
    TUploadedFile,
    TUploadingError,
    TErrorText,
} from "../types"

import { baseURL, development, isSnap } from '../constants'

import { statOnLoad } from '../Stat'

import Wrapper from "../elements/Wrapper"
import Content from "../elements/Content"

import {
    Buttons,
    UploadButtons,
    CloudButtons,
    ActionButtons,
    ButtonAction,
    ButtonAddFiles,
    ButtonClearFiles,
    ButtonOneDrive,
    ButtonDropBox,
    ButtonGoogleDrive,
} from "../elements/Buttons"

import { convert } from "../Services"

import DropZone from "../screens/DropZone"

const ErrorBlock = loadable(() => import('../elements/Errors'))
const FilesPreview = loadable(() => import('../screens/FilesPreview'))
const Preloader = loadable(() => import("../screens/Preloader"))
const Download = loadable(() => import("../screens/Download"))
const Error = loadable(() => import("../screens/Error"))

const uploadedFilesT: TUploadedFile[] = []
const uploadingErrors: TUploadingError[] = []
let downloadLink: string

const ConvertEXCELtoPDF = () => {
    const [currentScreen, switchScreen] = useState<TPossibleScreens>('filespreview')
    const [progress, setProgress] = useState(0)
    const [uploadedFiles, setUploadedFiles] = useState<TUploadedFile[]>(uploadedFilesT)
    const [countUploadingErrors, setCountUploadingErrors] = useState(uploadingErrors.length)
    const [errorText, setErrorText] = useState<TErrorText>({})
    const [showErrorBlock, setShowErrorBlock] = useState(true)    

    const upload: TUpload = {

        action: "excel_to_pdf",
        allowExtantions: ['xls', 'xlsx'],
        convertTo: "pdf",
        maxSize: 25, // MB
        multiselect: true,

        setDownloadLink: (link: string) => downloadLink = link,
        getDownloadLink: () => downloadLink,
        setProgress: (progress) => setProgress(progress),
        getProgress: () => progress,
        setScreen: (screen) => {
            if (window.scrollY > 220) window.scrollTo({ top: 0, behavior: "smooth" })
            if (screen === 'uploading' || screen === 'converting') setProgress(0)
            return switchScreen(screen)
        },
        getScreen: () => currentScreen,
        addFiles: (uploadedFile) => {
            return setUploadedFiles([...uploadedFiles, ...uploadedFile])
        },
        delFile: (uploadedFile) => {
            return setUploadedFiles(uploadedFiles.filter((element) => element !== uploadedFile))
        },
        clearFiles: () => setUploadedFiles([]),
        getFiles: () => uploadedFiles,
        moveFiles: (oldIndex, newIndex) => {
            return setUploadedFiles(arrayMoveImmutable(uploadedFiles, oldIndex, newIndex))
        },
        addError: (uploadingError) => {
            uploadingErrors.push(uploadingError)
            setShowErrorBlock(true)
            return setCountUploadingErrors(countUploadingErrors + 1)
        },
        delError: (uploadingError) => {
            const idx = uploadingErrors.indexOf(uploadingError)
            if (idx !== -1) {
                uploadingErrors.splice(idx, 1)
                return setCountUploadingErrors(countUploadingErrors - 1)
            }
        },
        clearErrors: () => {
            uploadingErrors.splice(0, uploadingErrors.length)
            return setCountUploadingErrors(0)
        },
        hideErrors: () => setShowErrorBlock(false),
        getErrors: () => uploadingErrors,
        setErrorText: setErrorText,
    }

    useEffect(() => {
        statOnLoad(upload)
    }, [])

    return (
        <Wrapper>
            <Helmet>
                <title>Convert XLS to PDF | Templateroller</title>
                <meta name="keywords" content="xls to pdf, convert xls to pdf, xls to pdf converter, how to convert xls to pdf, xls to pdf online converter, xlsx to pdf, convert xlsx to pdf, xlsx file to pdf, how to convert xlsx to pdf, xlsx to pdf converter, xlsx converter to pdf, excel to pdf, convert excel to pdf, how to convert excel to pdf, excel to pdf converter" />
                <meta name="description" content="Convert XLS to PDF with TemplateRoller's free Excel to PDF converter. This online tool works with XSL and XLSX files - no registration required." />
                <meta property='og:title' content="Convert XLS to PDF" />
                <meta property='og:description' content="Are you looking for an easy way to convert XLS to PDF? Drop your XLS or XLSX files into our XLS to PDF converter and save specific pages." />
                <meta property='og:url' content={`${baseURL}/tools/convert-excel-to-pdf.html`} />
                <meta property="og:image" content={`${baseURL}/tools/assets/og-excel-to-pdf.png`} />
                <meta property='og:locale' content="en_US" />
                <meta property='twitter:title' content="Convert XLS to PDF" />
                <meta property='twitter:description' content="Are you looking for an easy way to convert XLS to PDF? Drop your XLS or XLSX files into our XLS to PDF converter and save specific pages." />
                <meta property="twitter:image:src" content={`${baseURL}/tools/assets/og-excel-to-pdf.png`} />
                <link rel='canonical' href={`${baseURL}/tools/convert-excel-to-pdf.html`} />
            </Helmet>

            {
                currentScreen === 'filespreview' &&
                <h1>Convert Excel&nbsp;to&nbsp;PDF</h1>
            }

            {
                currentScreen === 'filespreview' &&
                uploadedFiles.length === 0 &&
                <Content>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <DropZone
                        title="Convert your XLS or XLSX files into PDF documents with ease and efficiency"
                        description="Convert XLS to PDF with TemplaterRoller’s free Excel to PDF converter. This online tool works with XSL and XLSX files — no registration required."
                        buttonText="Browse files"
                        warning={`Upload files of up to ${upload.maxSize} MB in ${(upload.allowExtantions.length > 2 ? upload.allowExtantions.map((e, i) => i == upload.allowExtantions.length - 1 ? `and .${e}` : `.${e}`) : upload.allowExtantions.map(e => `.${e}`)).join(', ')} format${upload.allowExtantions.length > 1 ? 's' : ''}`}
                        
                        upload={upload}
                    />
                    <Buttons>
                        <UploadButtons>
                            <CloudButtons>
                                <ButtonDropBox upload={upload} />
                                <ButtonOneDrive upload={upload} />
                                <ButtonGoogleDrive upload={upload} />
                            </CloudButtons>
                        </UploadButtons>
                    </Buttons>
                    
                </Content>
                
            }

            {
                currentScreen === 'filespreview' &&
                uploadedFiles.length > 0 &&
                <Content>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <FilesPreview upload={upload} />
                    <Buttons>
                        <UploadButtons>
                            <CloudButtons>
                                <ButtonDropBox upload={upload} />
                                <ButtonOneDrive upload={upload} />
                                <ButtonGoogleDrive upload={upload} />
                            </CloudButtons>
                            <ButtonAddFiles upload={upload} />
                            <ButtonClearFiles upload={upload} />
                        </UploadButtons>
                        <ActionButtons>
                            <ButtonAction
                                onClick={() => {
                                    return convert({
                                        upload: upload,
                                    })
                                }}
                            >Convert to {upload.convertTo.toUpperCase()}</ButtonAction>
                        </ActionButtons>
                    </Buttons>
                </Content>
            }

            {
                currentScreen === 'uploading' &&
                <Content>
                    <Preloader
                        type="progress"
                        title="Uploading Files"
                        description="Don’t close this tab, your files are being uploaded. This shouldn’t take long."
                        progress={progress}
                    />
                </Content>
            }

            {
                currentScreen === 'converting' &&
                <Content>
                    <Preloader
                        type="progress"
                        title="Converting Files"
                        description="Don’t close this tab, your files are being processed. This shouldn’t take long."
                        progress={progress}
                    />
                </Content>
            }

            {
                currentScreen === 'link' &&
                <Content>
                    <Download
                        title="Excel to PDF"
                        description="Your files have been converted to GIF. Click on “Download” to save the file to your device as a ZIP archive."
                        buttonText="Download"

                        upload={upload}
                        onClick={() => {
                            // @ts-ignore
                            window.stat?.(`tools_result_${upload.action}`)
                        }}
                    />
                </Content>
            }

            {
                currentScreen === 'error' &&
                <Content>
                    <Error text={errorText} />
                </Content>
            }

        </Wrapper>
    )
}

export default ConvertEXCELtoPDF